import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import styles from './ProfilePage.module.css';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import supabase from '../supabaseClient';
import ModifySubscriptionModal from '../components/ModifySubscriptionModal';
import { Spinner } from 'react-bootstrap';
import { useStripeDetails, useCancelCurrentSubscription, useRevertSubscriptions, useUncancelSubscription, usePaymentMethods, useStripePortal, useDeleteAccount } from '../hooks/stripeHooks';

// Helper function to convert price ID to plan name
const convertPriceIdToPlanName = (priceId) => {
    if (priceId === process.env.REACT_APP_MONTHLY_SUBSCRIPTION_PRICE_ID) {
        return 'Monthly';
    } else if (priceId === process.env.REACT_APP_ANNUAL_SUBSCRIPTION_PRICE_ID) {
        return 'Annual';
    }
    return 'Unknown';
};

const ProfilePage = () => {
    const { sessionToken, pgUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [supportSubject, setSupportSubject] = useState('');
    const [supportMessage, setSupportMessage] = useState('');
    const [passwordResetting, setPasswordResetting] = useState(false);
    const [passwordResetSuccess, setPasswordResetSuccess] = useState('');
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [stripeDetails, setStripeDetails] = useState(null);
    const [showModifySubscriptionModal, setShowModifySubscriptionModal] = useState(false);
    const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
    const [subscriptionDetailsMessage, setSubscriptionDetailsMessage] = useState('');
    const [modifySubscriptionAvailable, setModifySubscriptionAvailable] = useState(true);
    const [revertSubscriptionAvailable, setRevertSubscriptionAvailable] = useState(false);
    const [undoCancellationAvailable, setUndoCancellationAvailable] = useState(false);
    const [planMessage1, setPlanMessage1] = useState('');
    const { fetchStripeSubscriptionDetails, loading: fetchStripeSubscriptionDetailsLoading, error: fetchStripeSubscriptionDetailsError } = useStripeDetails();
    const { cancelAllSubscriptions, loading: cancelAllSubscriptionsLoading, error: cancelAllSubscriptionsError } = useCancelCurrentSubscription();
    const { revertSubscriptions, loading: revertSubscriptionsLoading, error: revertSubscriptionsError } = useRevertSubscriptions();
    const { uncancelSubscription, loading: uncancelSubscriptionLoading, error: uncancelSubscriptionError } = useUncancelSubscription();
    const { paymentMethods, loading: paymentMethodsLoading, error: paymentMethodsError } = usePaymentMethods();
    const { redirectToPortal, loading: portalLoading, error: portalError } = useStripePortal();
    const { deleteAccount, loading: deleteAccountLoading, error: deleteAccountError } = useDeleteAccount();
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

    const calculateMembershipDays = (emailConfirmedAt) => {
        if (!emailConfirmedAt) return null;
        const confirmedDate = new Date(emailConfirmedAt);
        const currentDate = new Date();
        const diffTime = Math.abs(currentDate - confirmedDate);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const checkCurrentSubscription = useCallback(async () => {
        if (!profile) return;
        console.log('Running checkCurrentSubscription');
        
        const details = await fetchStripeSubscriptionDetails();
        console.log('Stripe details fetched:', details);
        
        if (details?.currentSubscription) {
            console.log('Setting stripe details and current subscription:', details);
            setStripeDetails(details);
            setCurrentSubscription(details.currentSubscription);

            // Reset all states first
            setModifySubscriptionAvailable(false);
            setRevertSubscriptionAvailable(false);
            setUndoCancellationAvailable(false);

            if (details.currentSubscription.cancel_at_period_end) {
                // Subscription is scheduled to end
                if (details.scheduledSubscription) {
                    console.log('Found scheduled subscription:', details.scheduledSubscription);
                    // Case 2: Has scheduled new subscription
                    setSubscriptionDetailsMessage(
                        `You are currently on the ${convertPriceIdToPlanName(details.currentSubscription.price_id)} plan and will transition to the ${convertPriceIdToPlanName(details.scheduledSubscription.price_id)} plan on ${details.scheduledSubscription.start_date}. \nTo cancel this change, click here.`
                    );
                    setRevertSubscriptionAvailable(true);
                } else {
                    console.log('No scheduled subscription found, showing cancellation message');
                    // Case 3: Just cancellation, no new subscription
                    setSubscriptionDetailsMessage(
                        `Your subscription is scheduled to cancel at the end of your current billing period, which is on ${details.currentSubscription.cancel_at}. \nTo resume your subscription, click here.`
                    );
                    setUndoCancellationAvailable(true);
                }
            } else {
                // Case 1: Normal active subscription
                let tmpMessage = `You are currently on the ${convertPriceIdToPlanName(details.currentSubscription.price_id)} plan.`;
                if (details.currentSubscription.renewal_date) {
                    tmpMessage += ` Your subscription will renew on ${details.currentSubscription.renewal_date}.`;
                }
                setSubscriptionDetailsMessage(tmpMessage);
                setModifySubscriptionAvailable(true);
            }
        }
    }, [profile, convertPriceIdToPlanName]); // Remove fetchStripeSubscriptionDetails from deps

    const handleRevertSubscription = async () => {
        const stripeDetails = await fetchStripeSubscriptionDetails();

        let scheduledSubscriptionId = stripeDetails.scheduledSubscription.id;
        let subscriptionPendingCancellationId = stripeDetails.currentSubscription.id;

        console.log('scheduledSubscriptionId:', scheduledSubscriptionId);
        console.log('subscriptionPendingCancellationId:', subscriptionPendingCancellationId);

        await revertSubscriptions(subscriptionPendingCancellationId, scheduledSubscriptionId);
        
        // Refresh subscription details after reverting
        await checkCurrentSubscription();
    }

    const handleUncancelSubscription = async () => {
        await uncancelSubscription();
        // Refresh subscription details after uncancelling
        await checkCurrentSubscription();
    }

    const handleCancelSubscription = async () => {
        await cancelAllSubscriptions();
        setShowCancelSubscriptionModal(false);
        // Refresh subscription details after cancelling
        await checkCurrentSubscription();
    };

    const handleDeleteAccount = async () => {
        try {
            await deleteAccount();
            setShowDeleteAccountModal(false);
            // signOutUser in the hook will clear auth state and redirect to login
        } catch {
            // Error already set in hook
        }
    };


    useEffect(() => {
        const fetchProfile = async () => {
            try {
                console.log('Fetching profile for pgUser:', pgUser);
                // Get the current session for the auth token
                const { data: { session } } = await supabase.auth.getSession();
                
                if (!session?.access_token) {
                    throw new Error('No valid session found');
                }

                const response = await fetch(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/users/${pgUser.id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`
                    }
                });
                
                if (!response.ok) {
                    throw new Error('Failed to fetch profile');
                }
                const data = await response.json();
                console.log('Profile data fetched:', data[0]);
                setProfile(data[0]);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching profile:', err);
                setError('Failed to load profile. Please refresh the page or contact support.');
                setLoading(false);
            }
        };

        if (pgUser) {
            fetchProfile();
        } else {
            console.log('No pgUser available yet');
        }
    }, [pgUser]);

    // Separate useEffect for subscription check that runs when profile changes
    useEffect(() => {
        console.log('Profile changed, checking subscription. Profile:', profile);
        if (profile) {
            checkCurrentSubscription();
        }
    }, [profile, checkCurrentSubscription]);

    const handlePasswordReset = async () => {
        setPasswordResetting(true);
        setPasswordResetSuccess('');
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(profile.email);
            if (error) throw error;
            setPasswordResetSuccess('Password reset email sent! Please check your inbox.');
        } catch (err) {
            alert('Failed to send password reset email.');
            console.error(err);
        } finally {
            setPasswordResetting(false);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;
    if (!profile) return <div>No profile found</div>;

    return (
        <div className={styles.profileCard}>
            <div className={styles.profileHeader}>
                {/* Profile picture placeholder */}
                <div className={styles.profileAvatar}>
                    <h1 className={"mb-0"}>{profile.first_name[0]}{profile.last_name[0]}</h1>
                    {/* <img src={profile.avatar_url} alt={`${profile.first_name[0]}${profile.last_name[0]}`} /> */}
                </div>
                <div className={styles.profileNameRow}>
                    <span className={styles.profileName}>{profile.first_name} {profile.last_name}</span>
                    {/* Optionally add an edit icon here */}
                </div>
                {profile.email_confirmed_at && (
                    <div className={styles.membershipDuration}>
                        Member for {calculateMembershipDays(profile.email_confirmed_at)} {calculateMembershipDays(profile.email_confirmed_at) === 1 ? 'day' : 'days'}
                    </div>
                )}
            </div>
            <div className={styles.profileSection}>
                <label className={styles.profileLabel}>Email</label>
                <input
                    type="text"
                    value={profile.email}
                    className={styles.profileInput}
                    disabled
                />
            </div>
            <div className={styles.profileSection}>
                <label className={styles.profileLabel}>Password</label>
                <input
                    type="password"
                    value={"************"}
                    className={styles.profileInput}
                    disabled
                />
                <button 
                    className={styles.updatePasswordBtn}
                    onClick={handlePasswordReset}
                    type="button"
                >
                    {passwordResetting ? <Spinner style={{ color: '#52cd92' }} animation="border" size="sm" /> : "Update password"}
                </button>
                {passwordResetSuccess && <Alert variant="success" className="mt-3">{passwordResetSuccess}</Alert>}
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div className={styles.profileSection} style={{ flex: '1 1 300px' }}>
                    <label className={styles.profileLabel}>Subscription Details</label>
                    <div className={styles.subscriptionPlaceholder}>
                        {fetchStripeSubscriptionDetailsLoading ? (
                            <div>Loading subscription details...</div>
                        ) : fetchStripeSubscriptionDetailsError ? (
                            <div>Error loading subscription details: {fetchStripeSubscriptionDetailsError}</div>
                        ) : stripeDetails ? (
                            <div>
                                <p style={{ whiteSpace: 'pre-line' }}>{subscriptionDetailsMessage}</p>
                            </div>
                        ) : (
                            <div>Your subscription details will appear here.</div>
                        )}
                        {modifySubscriptionAvailable && (
                            <button 
                                className={styles.updatePasswordBtn}
                                onClick={() => setShowModifySubscriptionModal(true)}
                                type="button"
                            >
                                Modify Subscription
                            </button>
                        )}
                        {revertSubscriptionAvailable && (
                            <>
                                <button 
                                    className={styles.updatePasswordBtn}
                                    onClick={() => handleRevertSubscription()}
                                    type="button"
                                >
                                    {revertSubscriptionsLoading ? <Spinner style={{ color: '#52cd92' }} animation="border" size="sm" /> : "Revert Subscription"}
                                </button>
                                <div style={{ marginTop: '10px' }}>{revertSubscriptionsError && <Alert variant="danger">{revertSubscriptionsError}</Alert>}</div>
                            </>
                        )}
                        {undoCancellationAvailable && !revertSubscriptionAvailable && (
                            <button 
                                className={styles.updatePasswordBtn}
                                onClick={() => handleUncancelSubscription()}
                                type="button"
                            >
                                {uncancelSubscriptionLoading ? <Spinner style={{ color: '#52cd92' }} animation="border" size="sm" /> : "Resume Subscription"}
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.profileSection} style={{ flex: '1 1 300px' }}>
                    <label className={styles.profileLabel}>Payment Information</label>
                    <div className={styles.paymentPlaceholder}>
                        {paymentMethodsLoading ? (
                            <div>Loading payment details...</div>
                        ) : paymentMethodsError ? (
                            <div>Error loading payment details: {paymentMethodsError}</div>
                        ) : paymentMethods.length > 0 ? (
                            <div>
                                {paymentMethods.map(pm => (
                                    <div key={pm.id} className={styles.paymentMethod}>
                                        <div className={styles.paymentMethodDetails}>
                                            <span className={styles.cardBrand}>{pm.brand}</span>
                                            <span className={styles.cardNumber}>•••• {pm.last4}</span>
                                            <span className={styles.cardExpiry}>Expires {pm.expiryMonth}/{pm.expiryYear}</span>
                                        </div>
                                    </div>
                                ))}
                                <button 
                                    className={styles.updatePasswordBtn}
                                    onClick={redirectToPortal}
                                    disabled={portalLoading}
                                    type="button"
                                >
                                    {portalLoading ? <Spinner style={{ color: '#52cd92' }} animation="border" size="sm" /> : "Manage Payment Method"}
                                </button>
                                {portalError && <Alert variant="danger" className="mt-3">{portalError}</Alert>}
                            </div>
                        ) : (
                            <div>
                                <div>No payment methods found.</div>
                                <button 
                                    className={styles.updatePasswordBtn}
                                    onClick={redirectToPortal}
                                    disabled={portalLoading}
                                    type="button"
                                    style={{ marginTop: '1rem' }}
                                >
                                    {portalLoading ? <Spinner style={{ color: '#52cd92' }} animation="border" size="sm" /> : "Add Payment Method"}
                                </button>
                                {portalError && <Alert variant="danger" className="mt-3">{portalError}</Alert>}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.contactSection}>
                <label className={styles.profileLabel}>Need help? Contact our support team for assistance.</label>
                <button className={styles.contactBtn} type="button" onClick={() => setShowSupportModal(true)} style={{ alignSelf: 'center' }}>
                    Contact support
                </button>
            </div>
            <Modal show={showSupportModal} onHide={() => setShowSupportModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Contact Support</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="supportSubject">
                            <Form.Label>Subject</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter subject"
                                value={supportSubject}
                                onChange={e => setSupportSubject(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="supportMessage">
                            <Form.Label>Message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter your message"
                                value={supportMessage}
                                onChange={e => setSupportMessage(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSupportModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => setShowSupportModal(false)}>
                        Send
                    </Button>
                </Modal.Footer>
            </Modal>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <div className={styles.unsubscribeSection} style={{ flex: '1 1 100px' }}>
                    <div className={styles.unsubscribeWarning}>Unsubscribe from AutoChef</div>
                    <div className={styles.unsubscribeSubtext}>This will cancel your AutoChef subscription.</div>
                    <button className={styles.unsubscribeBtn} type="button" onClick={() => setShowCancelSubscriptionModal(true)}>Unsubscribe</button>
                </div>
                <div className={styles.deleteSection}>
                    <div className={styles.deleteWarning}>Delete account</div>
                    <div className={styles.deleteSubtext}>This will permanently delete your account and all associated data.</div>
                    <button className={styles.deleteAccountBtn} type="button" onClick={() => setShowDeleteAccountModal(true)}>Delete account</button>
                </div>
            </div>
            <ModifySubscriptionModal 
                show={showModifySubscriptionModal} 
                handleClose={() => setShowModifySubscriptionModal(false)} 
                currentPlanId={profile.price_id}
                currentSubscriptionId={profile.subscription_id}
            />
            <Modal 
                show={showCancelSubscriptionModal} 
                onHide={() => setShowCancelSubscriptionModal(false)}
                size="md"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Cancel Subscription</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-4">
                        <h5>Are you sure you want to cancel your subscription?</h5>
                        <p className="text-muted">
                            Your subscription will remain active until the end of your current billing period. 
                            After that, you'll lose access to AutoChef.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowCancelSubscriptionModal(false)}
                    >
                        Keep Subscription
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={() => handleCancelSubscription()}
                        disabled={cancelAllSubscriptionsLoading}
                    >
                        {cancelAllSubscriptionsLoading ? (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                        ) : (
                            'Cancel Subscription'
                        )}
                    </Button>
                    {cancelAllSubscriptionsError && <Alert variant="danger">{cancelAllSubscriptionsError}</Alert>}
                </Modal.Footer>
            </Modal>
            <Modal
                show={showDeleteAccountModal}
                onHide={() => !deleteAccountLoading && setShowDeleteAccountModal(false)}
                size="md"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Account</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-4">
                        <h5>Are you sure you want to delete your account?</h5>
                        <p className="text-muted">
                            This action cannot be undone. All your data including meal plans, pantry, preferences, and recipes will be permanently deleted.
                            Your subscription will also be cancelled.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteAccountModal(false)}
                        disabled={deleteAccountLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteAccount}
                        disabled={deleteAccountLoading}
                    >
                        {deleteAccountLoading ? (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                        ) : (
                            'Delete Account'
                        )}
                    </Button>
                    {deleteAccountError && <Alert variant="danger">{deleteAccountError}</Alert>}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ProfilePage;
